import { useCallback, useMemo } from 'react';
import {
  currencyDefinition,
  DEFAULT_CURRENCY_CODE,
  type CurrencyDefinition,
} from '@optiflow/types';
import { formatMoney, minorToInput, parseMoneyToMinor } from '@/modules/catalog/money';
import { useAuth } from './useAuth';
import { useLocale } from './useLocale';

export interface UseCurrencyResult {
  currencyCode: string;
  currency: CurrencyDefinition;
  /** Minor units to a display string, in the viewer's language and the tenant's precision. */
  format: (minor: number | null | undefined) => string;
  /** A typed major-unit amount to integer minor units, or null when unusable. */
  parse: (value: string) => number | null;
  /** Minor units back into an editable field value. */
  toInput: (minor: number | null | undefined) => string;
}

/**
 * Money in the tenant's own currency.
 *
 * Every price in the UI goes through this rather than naming a currency at the call site. A screen
 * that hardcodes one is wrong for every tenant that does not use it, and silently wrong — the
 * number still renders, it just means something else.
 */
export function useCurrency(): UseCurrencyResult {
  const { user } = useAuth();
  const { locale } = useLocale();

  const currencyCode = user?.tenant?.baseCurrencyCode ?? DEFAULT_CURRENCY_CODE;

  const format = useCallback(
    (minor: number | null | undefined) => formatMoney(minor, locale, currencyCode),
    [locale, currencyCode],
  );

  const parse = useCallback(
    (value: string) => parseMoneyToMinor(value, currencyCode),
    [currencyCode],
  );

  const toInput = useCallback(
    (minor: number | null | undefined) => minorToInput(minor, currencyCode),
    [currencyCode],
  );

  const currency = useMemo(() => currencyDefinition(currencyCode), [currencyCode]);

  return { currencyCode, currency, format, parse, toInput };
}
